<template>
  <div class="feature-flag-section" v-if="flags.length > 0">
    <!-- Section Content -->
    <div class="section-content">
      <!-- Section Header with Table Header Combined -->
      <div class="section-header-row">
        <div class="col-name">
          <h3 class="section-title">
            <i v-if="icon" :class="[icon, 'me-2']"></i>{{ title }}
          </h3>
        </div>
        <div class="col-description header-label">{{ description }}</div>
        <div class="col-status header-label">{{ statusLabel }}</div>
        <div class="col-actions header-label">{{ actionsLabel }}</div>
      </div>

      <!-- Table Rows -->
      <div 
        v-for="flag in sortedFlags" 
        :key="flag.id" 
        class="table-row"
        :class="{ 'inherited-row': flag.isInherited }"
        @click.stop="handleRowClick(flag)"
      >
        <div class="col-name">
          <div class="flag-name-wrapper">
            <span class="flag-name">{{ flag.name }}</span>
            <span v-if="flag.isInherited" class="badge-inherited">
              <i class="fas fa-link me-1"></i>Corporate Sync
              <span class="badge-inherited-tooltip">
                This module is inherited from Corporate level. Changes are automatically synchronized when the corporate setting is updated.
              </span>
            </span>
          </div>
        </div>
        <div class="col-description">
          <div class="description-wrapper">
            <template v-if="flag.isInherited">
              <div class="inherited-info-inline">
                <div class="inherited-info-icon">
                  <i class="fas fa-sync-alt"></i>
                </div>
                <div class="inherited-info-text">
                  <span class="inherited-info-label">Auto-synced from Corporate</span>
                  <span class="inherited-info-desc">This feature follows the corporate-level configuration and updates automatically.</span>
                </div>
              </div>
            </template>
            <template v-else>
              <span class="flag-description">{{ flag.description || 'No description provided' }}</span>
              <p v-if="isToggleDisabled(flag) && flag.feature === 'promotions_module'" class="feature-change-info">
                <i class="fas fa-info-circle me-1"></i>Automatically disabled because both <strong>Point Control</strong> and <strong>Benefit Module</strong> are NONACTIVE.
              </p>
              <p v-if="isToggleDisabled(flag) && flag.feature === 'voucher_gift'" class="feature-change-info">
                <i class="fas fa-info-circle me-1"></i>Automatically disabled because <strong>Point Control</strong> is NONACTIVE.
              </p>
            </template>
          </div>
        </div>
        <div class="col-status">
          <span class="status-pill" :class="[effectiveEnabled(flag) ? 'active' : 'inactive', { 'synced': flag.isInherited }]">
            <div class="status-dot"></div>
            {{ effectiveEnabled(flag) ? 'Active' : 'Inactive' }}
            <i v-if="flag.isInherited" class="fas fa-link status-sync-icon"></i>
          </span>
        </div>
        <div class="col-actions" @click.stop>
          <div class="actions-wrapper">
            <template v-if="flag.isInherited">
              <div class="inherited-lock-badge" title="Managed by Corporate">
                <i class="fas fa-lock"></i>
              </div>
            </template>
            <template v-else>
              <feature-flag-toggle
                :key="`toggle-${flag.id}-${toggleResetCounter[flag.id] || 0}`"
                :flag="flag"
                :disabled="isToggleDisabled(flag)"
                :loading="toggleLoading[flag.id]"
                :override-value="localChanges[flag.id] !== undefined ? localChanges[flag.id] : null"
                @toggle="handleToggle"
              />
            </template>
            <button 
              class="action-btn-audit"
              @click="handleViewAudit(flag.id)"
              title="View Audit Log"
            >
              <i class="fas fa-history"></i>
            </button>
            <i class="fas fa-chevron-right row-chevron"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FeatureFlagToggle from './FeatureFlagToggle.vue'

export default {
  name: 'FeatureFlagSection',
  
  components: {
    FeatureFlagToggle
  },

  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: 'DESCRIPTION'
    },
    statusLabel: {
      type: String,
      default: 'STATUS'
    },
    actionsLabel: {
      type: String,
      default: 'ACTIONS'
    },
    flags: {
      type: Array,
      default: () => []
    },
    searchQuery: {
      type: String,
      default: ''
    },
    toggleLoading: {
      type: Object,
      default: () => ({})
    },
    toggleResetCounter: {
      type: Object,
      default: () => ({})
    },
    allFlags: {
      type: Array,
      default: () => []
    },
    localChanges: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['toggle', 'view-audit', 'view-detail'],

  computed: {
    sortedFlags() {
      return [...this.flags].sort((a, b) => {
        // Non-inherited flags first, then inherited
        if (a.isInherited !== b.isInherited) {
          return a.isInherited ? 1 : -1
        }
        const nameA = a.name || ''
        const nameB = b.name || ''
        return nameA.localeCompare(nameB)
      })
    },
    hasInheritedFlags() {
      return this.flags.some(flag => flag.isInherited)
    }
  },

  methods: {
    handleToggle(flagId, newValue) {
      this.$emit('toggle', flagId, newValue)
    },
    
    handleViewAudit(flagId) {
      this.$emit('view-audit', flagId)
    },

    handleRowClick(flag) {
      if (flag.feature) {
        this.$emit('view-detail', flag.feature)
      }
    },

    effectiveEnabled(flag) {
      return this.localChanges[flag.id] !== undefined
        ? this.localChanges[flag.id]
        : flag.is_enabled
    },

    /**
     * Check if a specific toggle should be disabled
     * Promotions toggle is disabled when both point_control AND benefit_module are NONACTIVE
     */
    isToggleDisabled(flag) {
      if (flag.feature === 'promotions_module') {
        const searchIn = this.allFlags.length > 0 ? this.allFlags : this.flags
        const pointFlag = searchIn.find(f => f.feature === 'point_control')
        const benefitFlag = searchIn.find(f => f.feature === 'benefit_module')

        const isPointNonActive = pointFlag ? !this.effectiveEnabled(pointFlag) : true
        const isBenefitNonActive = benefitFlag ? !this.effectiveEnabled(benefitFlag) : true

        return isPointNonActive && isBenefitNonActive
      }

      if (flag.feature === 'voucher_gift') {
        const searchIn = this.allFlags.length > 0 ? this.allFlags : this.flags
        const pointFlag = searchIn.find(f => f.feature === 'point_control')
        const isPointNonActive = pointFlag ? !this.effectiveEnabled(pointFlag) : true

        return isPointNonActive
      }

      return false
    }
  }
}

</script>

<style scoped>
.feature-flag-section {
  margin-bottom: 40px;
}

.feature-flag-section:last-child {
  margin-bottom: 0;
}

/* Section Header */
.section-header-row {
  display: grid;
  grid-template-columns: 280px 1fr 140px 180px;
  gap: 20px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--extranet-primary);
  margin: 0;
  text-transform: capitalize;
  letter-spacing: 0;
}

.header-label {
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Section Content */
.section-content {
  background: white;
  border-radius: 8px;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}

/* Table Row */
.table-row {
  display: grid;
  grid-template-columns: 280px 1fr 140px 180px;
  gap: 20px;
  padding: 18px 24px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background 0.15s;
  cursor: pointer;
}

.table-row:hover {
  background: #f5f7ff;
}

.table-row:hover .row-chevron {
  opacity: 1;
  transform: translateX(2px);
}

.table-row.inherited-row {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f4 100%);
  border-left: 3px solid var(--extranet-primary, #b51a1f);
}

.table-row.inherited-row:hover {
  background: linear-gradient(135deg, #fff5f5 0%, #fef2f2 100%);
}

/* Column: Name */
.col-name {
  display: flex;
  align-items: center;
}

.flag-name-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flag-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  line-height: 1.4;
}

.inherited-row .flag-name {
  color: #475569;
}

.badge-inherited {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: var(--extranet-primary, #b51a1f);
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  cursor: help;
  transition: all 0.2s ease;
  border: 1px solid rgba(181, 26, 31, 0.15);
}

.badge-inherited:hover {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(181, 26, 31, 0.12);
}

.badge-inherited i {
  font-size: 9px;
}

.badge-inherited-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;
  background: #1e293b;
  color: #e2e8f0;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  white-space: normal;
  width: 260px;
  line-height: 1.5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  pointer-events: none;
}

.badge-inherited-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 24px;
  border: 6px solid transparent;
  border-top-color: #1e293b;
}

.badge-inherited:hover .badge-inherited-tooltip {
  display: block;
}

/* Inherited Inline Info */
.inherited-info-inline {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: rgba(181, 26, 31, 0.03);
  border: 1px solid rgba(181, 26, 31, 0.08);
  border-radius: 8px;
}

.inherited-info-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--extranet-primary, #b51a1f);
  font-size: 13px;
}

.inherited-info-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inherited-info-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--extranet-primary, #b51a1f);
  letter-spacing: 0.2px;
}

.inherited-info-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* Inherited Lock Badge */
.inherited-lock-badge {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #94a3b8;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  cursor: help;
}

/* Status Pill Synced Variant */
.status-pill.synced {
  gap: 6px;
}

.status-pill.synced.active {
  background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%);
  border: 1px solid rgba(22, 101, 52, 0.12);
}

.status-pill.synced.inactive {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid rgba(181, 26, 31, 0.1);
  color: var(--extranet-primary, #b51a1f);
}

.status-pill.synced.inactive .status-dot {
  background: var(--extranet-primary, #b51a1f);
  opacity: 0.5;
}

.status-sync-icon {
  font-size: 8px;
  opacity: 0.6;
}



/* Column: Description */
.col-description {
  display: flex;
  align-items: center;
}

.description-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.flag-description {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.feature-change-info {
  font-size: 13px;
  color: #d97706;
  background-color: #fffbeb;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #fcd34d;
  margin: 0;
  width: fit-content;
}

/* Column: Status */
.col-status {
  display: flex;
  align-items: center;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
}
.status-dot { width: 6px; height: 6px; border-radius: 50%; }
.status-pill.active { background: #dcfce7; color: #166534; }
.status-pill.active .status-dot { background: #166534; }
.status-pill.inactive { background: #f1f5f9; color: #64748b; }
.status-pill.inactive .status-dot { background: #94a3b8; }

/* Column: Actions */
.col-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.row-chevron {
  font-size: 11px;
  color: #94a3b8;
  opacity: 0;
  transition: all 0.2s;
  flex-shrink: 0;
}

.action-btn-audit {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  background: white;
  color: #9ca3af;
  transition: all 0.2s;
  cursor: pointer;
  font-size: 14px;
}

.action-btn-audit:hover {
  background: #f9fafb;
  color: #b51a1f;
  border-color: #b51a1f;
}



/* Responsive adjustments */
@media (max-width: 1200px) {
  .table-header-row,
  .table-row {
    grid-template-columns: 240px 1fr 120px 160px;
    gap: 16px;
  }
}
</style>
